import type {
  Task,
  TaskListItem,
  Requirement,
  Attachment,
  TaskComment,
  TaskHistoryEvent,
  Coordinates,
} from '../../types';
import { TaskStatus, Priority, TaskHistoryEventType } from '../../types'; // Regular import for enums
import { GeometryType } from '../../types/geometry';
import { generateId, extractUniqueSortedStrings } from '../../utils/helpers';
import { canAutoCompleteTask } from '../../utils/taskHelpers';
import { areGeometryArraysEqual } from '../../utils/geometryEditHelpers';
import {
  applyOpenDetailsPanel,
  applyDetailsPreviewOnMobile,
  applyCloseDetails,
  applyOpenFormPanel,
  applyCloseFormPanel,
  handleSelectItem,
  withShareFormGuard,
  type PanelDraftState,
} from '@/artifacts/panelHelpers';
import { tasksService } from '@/services/api';
import { historyService } from '@/services/api/historyService';
import { isGuid } from '@/utils/idHelpers';
import type { ApiError } from '@/types/api/errors';
import type { TaskFiltersParams } from '@/types/api/requests';
import {
  buildTaskFiltersParams,
  type TaskFiltersSlice,
} from './taskFiltersSlice';
import {
  mapTaskListItemFromResponse,
  mapTaskResponseToListItem,
  mapTaskForMap,
  mapRelatedTaskSummaryFromResponse,
} from '../mappers/taskMapper';
import { mapCommentFromApi } from '../mappers/commentMapper';
import { mapGeometryFromApi } from '../mappers/geometryMapper';

// ===== CONSTANTS =====

/** Items per page for kanban columns (smaller than list's DEFAULT_PAGE_SIZE) */
const KANBAN_PAGE_SIZE = 10;

// ===== SLICE INTERFACE =====

export interface TasksSlice {
  tasks: {
    items: TaskListItem[];
    selectedId: string | null;

    // Pagination
    hasNextPage: boolean;
    totalCount: number;
    currentPage: number;

    // Detail (full type, on demand)
    selectedTask: Task | null;

    // Map (full geometries for rendering)
    mapTasks: Task[];

    // API state
    isLoading: boolean;
    hasLoaded: boolean;
    isSyncing: boolean;
    error: ApiError | null;
    lastSyncAt: Date | null;
    _lastFetchFilters: TaskFiltersParams | undefined;

    // ===== ArtifactSlice contract (R1 of artifact registry refactor) =====
    /** Whether the task details panel is open. */
    detailsIsOpen: boolean;
    /** Whether the task form (create/edit) is open. */
    formIsOpen: boolean;
    /** Form mode discriminator. */
    formMode: 'create' | 'edit' | null;
    /** Id of the task currently being edited (null in create mode). */
    formEditingId: string | null;
    /** Anchor point for the form (GPS detection + drag handle). */
    formAnchorPoint: Coordinates | null;
    /** Whether the form has unsaved user input (synced from TaskFormWizard). */
    formIsDirty: boolean;

    /** Open the task details panel. Closes any open form and the cross-artifact panels. */
    openDetails: () => void;
    /** Close the task details panel. Clears `selectedId` so the URL→store guard works. */
    closeDetails: () => void;
    /**
     * Open the task form in create or edit mode.
     * Triggers AUTO-* side effects (clear geometries, hide other markers, reset navigation panel).
     * Routes through `formConflictSlice` if another artifact's form is open.
     */
    openForm: (mode: 'create' | 'edit', itemId?: string) => void;
    /**
     * Close the task form. Triggers AUTO-* cleanup (reset tool, show markers, clear geometries,
     * reset preferences) and re-opens task details if a task is still selected.
     */
    closeForm: () => void;
    /** Slice-specific helper: set the form anchor point without opening/closing the form. */
    setFormAnchorPoint: (point: Coordinates | null) => void;
    /** Update the dirty flag from the active form component. */
    setFormIsDirty: (value: boolean) => void;
    /** Sidebar entry point for creating a task. Routes through the unsaved-changes guard so a dirty competing form prompts before discard. */
    startCreateFlow: () => void;
    /** ArtifactSlice contract: thin alias over `selectTask` for registry-driven overlay open. */
    selectItem: (id: string) => void;

    // CRUD Operations
    createTask: (
      taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
    ) => string; // Returns task ID to enable auto-selection after creation
    updateTask: (id: string, updates: Partial<Task>, batchId?: string) => void;
    deleteTask: (id: string) => void;
    selectTask: (id: string | null) => void;
    setTaskStatus: (id: string, status: TaskStatus) => void;

    // Requirements
    addRequirement: (
      taskId: string,
      requirement: Omit<Requirement, 'id' | 'createdAt'>,
      batchId?: string
    ) => void;
    updateRequirement: (
      taskId: string,
      requirementId: string,
      updates: Partial<Requirement>,
      batchId?: string
    ) => void;
    deleteRequirement: (
      taskId: string,
      requirementId: string,
      batchId?: string
    ) => void;

    // Attachments
    addAttachment: (
      taskId: string,
      attachment: Omit<Attachment, 'id' | 'uploadedAt'>,
      batchId?: string
    ) => void;
    deleteAttachment: (
      taskId: string,
      attachmentId: string,
      batchId?: string
    ) => void;

    // Comments
    addComment: (
      taskId: string,
      comment: Omit<TaskComment, 'id' | 'createdAt'>
    ) => void;
    receiveComment: (taskId: string, comment: TaskComment) => void;
    updateComment: (
      taskId: string,
      commentId: string,
      updates: Partial<TaskComment>
    ) => void;
    deleteComment: (taskId: string, commentId: string) => void;

    // History
    addHistoryEvent: (
      taskId: string,
      event: Omit<TaskHistoryEvent, 'id' | 'createdAt'>
    ) => void;

    // Selectors
    getTaskById: (id: string) => TaskListItem | undefined;
    getTasksByStatus: (status: TaskStatus) => TaskListItem[];
    getCompletionPercentage: (taskId: string) => number;
    getUniqueLocations: () => string[];

    // API actions
    fetchTasks: () => Promise<void>;
    loadMoreTasks: () => Promise<void>;
    fetchTaskById: (id: string) => Promise<void>;
    fetchTasksForMap: () => Promise<void>;
    /** Replaces mapTasks wholesale — used by public share hydration. */
    setMapTasks: (tasks: Task[]) => void;
    createTaskApi: (
      taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
    ) => Promise<string | null>;
    updateTaskApi: (id: string, updates: Partial<Task>) => Promise<boolean>;
    deleteTaskApi: (id: string) => Promise<boolean>;

    // API state helpers
    setLoading: (loading: boolean) => void;
    setError: (error: ApiError | null) => void;
    clearError: () => void;

    // Kanban per-status pagination
    kanban: {
      columns: Record<
        string,
        {
          items: TaskListItem[];
          currentPage: number;
          hasNextPage: boolean;
          totalCount: number;
          isLoading: boolean;
        }
      >;
    };
    fetchKanbanData: () => Promise<void>;
    loadMoreKanbanColumn: (status: TaskStatus) => Promise<void>;
  };
}

// ===== TYPES FOR GET PARAMETER =====
// TasksSlice needs access to auth.user.displayName for history events
// and to other slices for the ArtifactSlice AUTO-* side effects.
//
// Note: drawing-tool actions (clearGeometries, setActiveTool, ...) live at the
// top level of the composed store (no `drawingTools.` prefix) because the
// drawingToolsSlice spreads its state directly. Same for `resetNavigationPanel`.
interface StoreWithUser {
  tasks: TasksSlice['tasks'];
  taskFilters: TaskFiltersSlice['taskFilters'];
  auth: {
    user: { displayName: string; avatarUrl: string | null } | null;
  };
  notes: {
    formIsOpen: boolean;
    detailsIsOpen: boolean;
    closeDetails: () => void;
    closeForm: () => void;
  };
  shareForm: { formIsOpen: boolean };
  formConflict: { request: (onProceed: () => void) => void };
  ui: {
    sidePanel: { isOpen: boolean };
    slidePanel: {
      isMinimized: boolean;
      previewMode: boolean;
      wasMinimizedBeforeGeometriesList: boolean;
    };
    geometriesList: { isOpen: boolean };
    mapOverlay: { isOpen: boolean };
    isSelectingTaskLocation: boolean;
    startTaskLocationSelection: () => void;
  };
  // Drawing tool actions exposed at top level by drawingToolsSlice
  clearGeometries: () => void;
  setActiveTool: (tool: GeometryType) => void;
  setHideOtherMarkers: (value: boolean) => void;
  clearSelection: () => void;
  resetNavigationPanel: () => void;
  toolPreferences: {
    resetAllPreferences: () => void;
  };
}

// ===== HELPER FUNCTIONS =====

// Helper to get current user info for history events
const getCurrentAuthor = (
  get: () => StoreWithUser
): { name: string; avatarUrl: string | null } => {
  const user = get().auth.user;
  return {
    name: user?.displayName ?? 'System',
    avatarUrl: user?.avatarUrl ?? null,
  };
};

// Helper to truncate text for history descriptions
const truncateText = (text: string, maxLength = 50): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Compare two Date objects by value (not reference)
 *
 * Prevents false history events when dates are recreated with same value.
 * Date parsing creates new Date objects, so reference equality fails.
 *
 * @param date1 - First date (or null/undefined)
 * @param date2 - Second date (or null/undefined)
 * @returns true if dates represent the same timestamp
 */
const areDatesEqual = (
  date1: Date | null | undefined,
  date2: Date | null | undefined
): boolean => {
  if (date1 === date2) return true; // Same reference or both null/undefined
  if (!date1 || !date2) return false; // One is null/undefined, other isn't
  return date1.getTime() === date2.getTime(); // Compare timestamps
};

// Helper to add history event to task
const addHistoryEventToTask = (
  task: Task,
  eventType: TaskHistoryEventType,
  description: string,
  author: { name: string; avatarUrl: string | null },
  metadata?: TaskHistoryEvent['metadata']
): void => {
  task.history.push({
    id: generateId(),
    taskId: task.id,
    eventType,
    description,
    author: author.name,
    authorAvatarUrl: author.avatarUrl,
    createdAt: new Date(),
    metadata,
  });
};

// Helper to determine task status from requirements completion
const determineTaskStatusFromRequirements = (task: Task): TaskStatus => {
  const completedRequirements = task.requirements.filter(
    (req) => req.completed
  ).length;
  const totalRequirements = task.requirements.length;

  // If no requirements, keep current status
  if (totalRequirements === 0) {
    return task.status;
  }

  // If all requirements are completed, set to completed
  if (completedRequirements === totalRequirements) {
    return TaskStatus.Completed;
  }

  // If current status is completed but not all requirements are done
  if (
    task.status === TaskStatus.Completed &&
    completedRequirements < totalRequirements
  ) {
    // Check if task is overdue
    if (task.dueDate && new Date(task.dueDate) < new Date()) {
      return TaskStatus.Delayed;
    }
    return TaskStatus.InProgress;
  }

  // Otherwise, keep current status
  return task.status;
};

/**
 * Sync lightweight fields from selectedTask back to the matching item in items[].
 * Keeps the list view up-to-date after mutations on the detail view.
 */
const syncSelectedTaskToListItem = (state: TasksSlice): void => {
  const selected = state.tasks.selectedTask;
  if (!selected) return;

  const listItem = state.tasks.items.find((item) => item.id === selected.id);
  if (!listItem) return;

  listItem.title = selected.title;
  listItem.status = selected.status;
  listItem.priority = selected.priority;
  listItem.assignedToId = selected.assignedToId;
  listItem.assignedToName = selected.assignedToName;
  listItem.assignedToAvatarUrl = selected.assignedToAvatarUrl;
  listItem.assignedToIsFormerMember = selected.assignedToIsFormerMember;
  listItem.dueDate = selected.dueDate;
  listItem.location = selected.location;
  listItem.anchorPoint = selected.anchorPoint;
  listItem.requirementCount = selected.requirements.length;
  listItem.completedRequirementCount = selected.requirements.filter(
    (req) => req.completed
  ).length;
  listItem.attachmentCount = selected.attachments.length;
  listItem.commentCount = selected.comments.length;
  listItem.updatedAt = selected.updatedAt;
};

// ===== SLICE CREATOR =====
// Phase 2: Full implementation with Immer v10
//
// Note: Immer wraps the entire composed store, so the underlying mutation
// surface includes every slice. The `set` parameter is widened to allow
// the new ArtifactSlice actions to mutate cross-slice flags (e.g.
// `state.ui.slidePanel.previewMode`) inside the same draft transaction.
type TasksSliceSetState = TasksSlice &
  PanelDraftState & {
    ui: {
      slidePanel: { wasMinimizedBeforeGeometriesList: boolean };
      geometriesList: { isOpen: boolean };
      mapOverlay: { isOpen: boolean };
      isSelectingTaskLocation: boolean;
    };
  };

export const createTasksSlice = (
  set: (fn: (state: TasksSliceSetState) => void) => void,
  get: () => StoreWithUser,
  _store: unknown
) => ({
  tasks: {
    items: [] as TaskListItem[],
    selectedId: null as string | null,

    // Pagination initial values
    hasNextPage: false,
    totalCount: 0,
    currentPage: 0,

    // Detail view
    selectedTask: null as Task | null,

    // Map tasks (full geometries)
    mapTasks: [] as Task[],

    // API state initial values
    isLoading: false,
    hasLoaded: false,
    isSyncing: false,
    error: null as ApiError | null,
    lastSyncAt: null as Date | null,
    _lastFetchFilters: undefined as TaskFiltersParams | undefined,

    // ===== ArtifactSlice contract (R1) =====
    detailsIsOpen: false,
    formIsOpen: false,
    formMode: null as 'create' | 'edit' | null,
    formEditingId: null as string | null,
    formAnchorPoint: null as Coordinates | null,
    formIsDirty: false,

    // Kanban per-status pagination
    kanban: {
      columns: {} as Record<
        string,
        {
          items: TaskListItem[];
          currentPage: number;
          hasNextPage: boolean;
          totalCount: number;
          isLoading: boolean;
        }
      >,
    },

    // ===== PHASE 7a: CRUD + STATUS =====

    createTask: (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
      let createdTaskId = '';

      set((state) => {
        const newTask: Task = {
          ...taskData,
          id: generateId(),
          createdAt: new Date(),
          updatedAt: null,
          comments: taskData.comments || [],
          history: [],
          relatedTasks: [],
          requireRelatedTasksCompletion:
            taskData.requireRelatedTasksCompletion || false,
        };

        // Capture task ID for return value
        createdTaskId = newTask.id;

        // Add initial "TaskCreated" history event
        addHistoryEventToTask(
          newTask,
          TaskHistoryEventType.TaskCreated,
          'Task created',
          getCurrentAuthor(get),
          {
            initialTask: true,
            title: newTask.title,
            priority: newTask.priority,
            status: newTask.status,
          }
        );

        // Add lightweight item to list
        state.tasks.items.push({
          id: newTask.id,
          shortId: newTask.shortId,
          title: newTask.title,
          status: newTask.status,
          priority: newTask.priority,
          assignedToId: newTask.assignedToId,
          assignedToName: newTask.assignedToName,
          assignedToAvatarUrl: newTask.assignedToAvatarUrl,
          assignedToIsFormerMember: newTask.assignedToIsFormerMember,
          startDate: newTask.startDate,
          completedAt: newTask.completedAt,
          dueDate: newTask.dueDate,
          location: newTask.location,
          anchorPoint: newTask.anchorPoint,
          requirementCount: newTask.requirements.length,
          completedRequirementCount: newTask.requirements.filter(
            (req) => req.completed
          ).length,
          attachmentCount: newTask.attachments.length,
          commentCount: newTask.comments.length,
          updatedAt: newTask.updatedAt,
        });

        state.tasks.totalCount += 1;
      });

      return createdTaskId; // Return created task ID for auto-selection
    },

    updateTask: (id: string, updates: Partial<Task>, batchId?: string) => {
      set((state) => {
        // Operate on selectedTask if it matches
        if (state.tasks.selectedTask?.id !== id) return;
        const existingTask = state.tasks.selectedTask;

        // Store old values for history
        const oldTitle = existingTask.title;
        const oldDescription = existingTask.description;
        const oldPriority = existingTask.priority;
        const oldAssignedToId = existingTask.assignedToId;
        const oldDueDate = existingTask.dueDate;
        const oldGeometries = existingTask.geometries;

        // Detect multiple field changes for batching
        const formEditFields = [
          'title',
          'description',
          'priority',
          'assignedToId',
          'dueDate',
          'geometries',
        ];
        const changedFields = Object.keys(updates).filter((field) =>
          formEditFields.includes(field)
        );
        const finalBatchId =
          batchId || (changedFields.length > 1 ? generateId() : undefined);

        // Apply updates to selectedTask
        const newUpdatedAt = new Date();
        Object.assign(existingTask, updates, { updatedAt: newUpdatedAt });

        // Add history events for changed fields
        if ('title' in updates && oldTitle !== updates.title) {
          const description = finalBatchId
            ? 'Changed title'
            : `Changed title from "${oldTitle}" to "${updates.title}"`;
          addHistoryEventToTask(
            existingTask,
            TaskHistoryEventType.TitleChanged,
            description,
            getCurrentAuthor(get),
            {
              field: 'title',
              oldValue: oldTitle,
              newValue: updates.title,
              batchId: finalBatchId,
            }
          );
        }

        if (
          'description' in updates &&
          oldDescription !== updates.description
        ) {
          const description = finalBatchId
            ? 'Updated description'
            : 'Updated task description';
          addHistoryEventToTask(
            existingTask,
            TaskHistoryEventType.DescriptionChanged,
            description,
            getCurrentAuthor(get),
            {
              field: 'description',
              oldValue: truncateText(oldDescription),
              newValue: truncateText(updates.description || ''),
              batchId: finalBatchId,
            }
          );
        }

        if ('priority' in updates && oldPriority !== updates.priority) {
          const description = finalBatchId
            ? 'Changed priority'
            : `Changed priority from ${oldPriority} to ${updates.priority}`;
          addHistoryEventToTask(
            existingTask,
            TaskHistoryEventType.PriorityChanged,
            description,
            getCurrentAuthor(get),
            {
              field: 'priority',
              oldValue: oldPriority,
              newValue: updates.priority,
              batchId: finalBatchId,
            }
          );
        }

        if (
          'assignedToId' in updates &&
          oldAssignedToId !== updates.assignedToId
        ) {
          const description = finalBatchId
            ? 'Changed assignee'
            : `Changed assignee from "${oldAssignedToId}" to "${updates.assignedToId}"`;
          addHistoryEventToTask(
            existingTask,
            TaskHistoryEventType.AssigneeChanged,
            description,
            getCurrentAuthor(get),
            {
              field: 'assignedToId',
              oldValue: oldAssignedToId,
              newValue: updates.assignedToId as string | null,
              batchId: finalBatchId,
            }
          );
        }

        // Only create history event if dueDate value actually changed (not just object reference)
        if (
          'dueDate' in updates &&
          !areDatesEqual(oldDueDate, updates.dueDate)
        ) {
          const description = 'Changed due date';
          addHistoryEventToTask(
            existingTask,
            TaskHistoryEventType.DueDateChanged,
            description,
            getCurrentAuthor(get),
            {
              field: 'dueDate',
              oldValue: oldDueDate,
              newValue: updates.dueDate,
              batchId: finalBatchId,
            }
          );
        }

        // Track map representation changes (simplified for multi-geometry support)
        // Only create history event if geometries actually changed (deep comparison)
        if (
          'geometries' in updates &&
          !areGeometryArraysEqual(oldGeometries, updates.geometries)
        ) {
          addHistoryEventToTask(
            existingTask,
            TaskHistoryEventType.MapRepresentationChanged,
            '', // Translation comes from i18n (history.events.mapRepresentationChanged)
            getCurrentAuthor(get),
            { field: 'geometries', batchId: finalBatchId }
          );
        }

        // Sync lightweight fields back to items[]
        syncSelectedTaskToListItem(state);
      });
    },

    deleteTask: (id: string) => {
      set((state) => {
        state.tasks.items = state.tasks.items.filter((task) => task.id !== id);
        state.tasks.mapTasks = state.tasks.mapTasks.filter((t) => t.id !== id);
        if (state.tasks.selectedId === id) {
          state.tasks.selectedId = null;
        }
        if (state.tasks.selectedTask?.id === id) {
          state.tasks.selectedTask = null;
        }
        state.tasks.totalCount = Math.max(0, state.tasks.totalCount - 1);
      });
    },

    selectTask: (id: string | null) => {
      handleSelectItem(
        set,
        id,
        (draft, val) => {
          draft.tasks.selectedId = val;
        },
        get().tasks.selectedTask,
        (fetchId) => get().tasks.fetchTaskById(fetchId),
        (draft) => {
          draft.tasks.selectedTask = null;
        }
      );
    },

    selectItem: (id: string): void => {
      get().tasks.selectTask(id);
    },

    // ===== ArtifactSlice actions (migrated from uiSlice in R1) =====

    /**
     * Open the task details panel.
     *
     * Side effects (migrated one-to-one from `uiSlice.toggleTaskDetails(true)`):
     * - Cross-artifact mutual exclusion: closes notes details + form.
     * - Closes the task form (form ↔ details exclusion) and resets `slidePanel.previewMode`.
     * - On mobile: opens preview mode and closes the SidePanel for the task list.
     */
    openDetails: () => {
      // Single right-panel rule: guard-close the share config form (prompts
      // when dirty) before revealing the details panel.
      withShareFormGuard(get(), () => {
        const store = get();

        // Cross-artifact mutual exclusion
        store.notes.closeDetails();
        store.notes.closeForm();

        set((state) => {
          // Form ↔ details mutual exclusion
          state.tasks.formIsOpen = false;
          state.tasks.formMode = null;

          state.tasks.detailsIsOpen = true;
          applyOpenDetailsPanel(state);
          applyDetailsPreviewOnMobile(state);
        });
      });
    },

    /**
     * Close the task details panel.
     *
     * Side effects (from `uiSlice.toggleTaskDetails(false)` close branch):
     * - Resets `slidePanel.previewMode`.
     * - Clears `tasks.selectedId` (URL→store guard relies on this).
     */
    closeDetails: () => {
      set((state) => applyCloseDetails(state, state.tasks));
    },

    /**
     * Open the task form in `create` or `edit` mode.
     *
     * Side effects (migrated from `uiSlice.toggleTaskForm({ open: true })`):
     * - Unsaved-changes guard via `formConflict.request`.
     * - Cross-artifact mutual exclusion: closes notes details + form.
     * - Closes task details (form ↔ details exclusion) + resets `slidePanel.previewMode`.
     * - On mobile create/edit: minimises the slide panel before render.
     * - Cancels `isSelectingTaskLocation`.
     * - AUTO-* drawing tool side effects: clears geometries (create), resets navigation panel,
     *   hides other markers.
     */
    openForm: (mode: 'create' | 'edit', itemId?: string) => {
      // Idempotent re-open: re-syncing the already-open form (e.g. the panel
      // remount on a map ↔ list/kanban view switch) is a no-op. It must not
      // hit the unsaved-changes guard (which would falsely prompt) nor rewrite
      // formMode (which would remount the panel and reset its size/fullscreen).
      const current = get().tasks;
      if (
        current.formIsOpen &&
        current.formMode === mode &&
        current.formEditingId === (mode === 'edit' ? (itemId ?? null) : null)
      ) {
        return;
      }

      // Route through the universal unsaved-changes guard: a dirty competing
      // form prompts before discard; clean forms close silently first.
      get().formConflict.request(() => {
        const store = get();

        // Cross-artifact mutual exclusion
        store.notes.closeDetails();
        store.notes.closeForm();

        set((state) => {
          // Mobile minimise + previewMode reset BEFORE opening the panel
          // (matches the legacy ordering required to avoid first-render flicker)
          applyOpenFormPanel(state);

          // Set form-open state
          state.tasks.formIsOpen = true;
          state.tasks.formMode = mode;
          state.tasks.formEditingId = mode === 'edit' ? (itemId ?? null) : null;
          state.tasks.formIsDirty = false;

          // Cancel pending location selection on form open
          if (state.ui.isSelectingTaskLocation) {
            state.ui.isSelectingTaskLocation = false;
          }

          // Form ↔ details mutual exclusion
          state.tasks.detailsIsOpen = false;
        });

        // AUTO-CLEAR: Reset previous geometries when entering CREATE mode
        if (mode === 'create') {
          store.clearGeometries();
        }

        // AUTO-RESET: Default tool back to navigation
        store.resetNavigationPanel();

        // AUTO-ENABLE: Hide other markers so the user can focus on the edited/created task
        store.setHideOtherMarkers(true);
      });
    },

    /**
     * Close the task form.
     *
     * Side effects (migrated from `uiSlice.toggleTaskForm({ open: false })`):
     * - Resets active tool to None, shows other markers, clears geometries + selection.
     * - Resets tool preferences to defaults.
     * - Resets navigation panel.
     * - AUTO: if a task is still selected (edit flow or post-save), reopens task details.
     *   Otherwise clears selection (cancel-after-create flow).
     * - Closes geometries list, mapOverlay, resets minimise + previewMode flags.
     */
    closeForm: () => {
      if (!get().tasks.formIsOpen) return;

      const store = get();
      const hadSelectedTask = !!store.tasks.selectedId;

      set((state) => {
        state.tasks.formIsOpen = false;
        state.tasks.formMode = null;
        state.tasks.formEditingId = null;
        state.tasks.formAnchorPoint = null;
        state.tasks.formIsDirty = false;
        state.ui.geometriesList.isOpen = false;
        applyCloseFormPanel(state);
        state.ui.slidePanel.wasMinimizedBeforeGeometriesList = false;
        state.ui.mapOverlay.isOpen = false;
      });

      // AUTO-DISABLE side effects (mirror uiSlice.toggleTaskForm close branch)
      store.setActiveTool(GeometryType.None);
      store.setHideOtherMarkers(false);
      store.clearGeometries();
      store.clearSelection();
      store.toolPreferences.resetAllPreferences();
      store.resetNavigationPanel();

      // MODE-AWARE CLEANUP: reopen details if a task was selected,
      // otherwise clear selection (cancel-after-create flow).
      if (hadSelectedTask) {
        set((state) => {
          state.tasks.detailsIsOpen = true;
          applyOpenDetailsPanel(state);
          applyDetailsPreviewOnMobile(state);
        });
      } else {
        get().tasks.selectTask(null);
      }
    },

    /**
     * Slice-specific helper: set the form anchor point without opening the form.
     * (Not part of the shared `ArtifactSlice` contract — kept for symmetry with notes.)
     */
    setFormAnchorPoint: (point: Coordinates | null) => {
      set((state) => {
        state.tasks.formAnchorPoint = point;
      });
    },

    setFormIsDirty: (value: boolean) => {
      // Guard a no-op write: useWizardNavigation re-syncs on every form
      // keystroke and Mantine forms create a new `values` ref each time, so
      // an unguarded write would notify every subscriber on each keystroke.
      if (get().tasks.formIsDirty === value) return;
      set((state) => {
        state.tasks.formIsDirty = value;
      });
    },

    /** Sidebar entry point for creating a task. Routes through the unsaved-changes guard so a dirty competing form prompts before discard. */
    startCreateFlow: () => {
      get().formConflict.request(() => {
        const store = get();
        // The guard already closed clean competing FORMS; close remaining
        // details panels for parity with openForm's cross-artifact exclusion.
        store.notes.closeDetails();

        store.ui.startTaskLocationSelection();
      });
    },

    setTaskStatus: (id: string, status: TaskStatus) => {
      set((state) => {
        const now = new Date();

        // Update selectedTask if it matches
        if (state.tasks.selectedTask?.id === id) {
          const task = state.tasks.selectedTask;
          if (task.status === status) return;

          const oldStatus = task.status;
          task.status = status;
          task.updatedAt = now;

          // Add status change history event
          addHistoryEventToTask(
            task,
            TaskHistoryEventType.StatusChanged,
            `Changed status from ${oldStatus} to ${status}`,
            getCurrentAuthor(get),
            { oldValue: oldStatus, newValue: status, fieldName: 'status' }
          );

          syncSelectedTaskToListItem(state);
        }

        // Also update the list item directly (status is on TaskListItem)
        const listItem = state.tasks.items.find((item) => item.id === id);
        if (listItem) {
          listItem.status = status;
          listItem.updatedAt = now;
        }

        // Sync status to map markers (drives symbol layer color)
        const mapTask = state.tasks.mapTasks.find(
          (mapMarker) => mapMarker.id === id
        );
        if (mapTask) {
          mapTask.status = status;
          mapTask.updatedAt = now;
        }

        // Kanban renders from a separate paginated sub-store; without this the
        // optimistic update is invisible and the card snaps back on drop.
        const kanbanColumns = state.tasks.kanban.columns;
        let sourceLocation: { columnKey: string; index: number } | undefined;

        for (const [columnKey, column] of Object.entries(kanbanColumns)) {
          const index = column.items.findIndex(
            (taskInColumn) => taskInColumn.id === id
          );
          if (index !== -1) {
            sourceLocation = { columnKey, index };
            break;
          }
        }

        if (!sourceLocation || sourceLocation.columnKey === status) return;

        const sourceColumn = kanbanColumns[sourceLocation.columnKey];
        const targetColumn = kanbanColumns[status];
        if (!sourceColumn || !targetColumn) return;

        const [movedItem] = sourceColumn.items.splice(sourceLocation.index, 1);
        sourceColumn.totalCount -= 1;
        if (!movedItem) return;

        movedItem.status = status;
        movedItem.updatedAt = now;
        targetColumn.items.unshift(movedItem);
        targetColumn.totalCount += 1;
      });
    },

    // ===== PHASE 7b: REQUIREMENTS =====

    addRequirement: (
      taskId: string,
      requirement: Omit<Requirement, 'id' | 'createdAt'>,
      batchId?: string
    ) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const newRequirement: Requirement = {
          ...requirement,
          id: generateId(),
          createdAt: new Date(),
        };

        task.requirements.push(newRequirement);
        task.updatedAt = new Date();

        // Add requirement added history event
        addHistoryEventToTask(
          task,
          TaskHistoryEventType.RequirementAdded,
          `Added requirement: "${truncateText(newRequirement.text, 40)}"`,
          getCurrentAuthor(get),
          {
            requirementId: newRequirement.id,
            requirementText: newRequirement.text,
            requirementOrder: newRequirement.order,
            batchId,
          }
        );

        // Auto-update task status based on requirements completion
        const newStatus = determineTaskStatusFromRequirements(task);
        if (newStatus !== task.status) {
          const oldStatus = task.status;
          task.status = newStatus;

          addHistoryEventToTask(
            task,
            TaskHistoryEventType.StatusChanged,
            `Status automatically changed from ${oldStatus} to ${newStatus} due to requirement addition`,
            getCurrentAuthor(get),
            {
              oldValue: oldStatus,
              newValue: newStatus,
              fieldName: 'status',
              reason: 'requirement_added',
              batchId,
            }
          );
        }

        // Sync counts back to list item
        syncSelectedTaskToListItem(state);
      });
    },

    updateRequirement: (
      taskId: string,
      requirementId: string,
      updates: Partial<Requirement>,
      batchId?: string
    ) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const requirement = task.requirements.find(
          (req) => req.id === requirementId
        );
        if (!requirement) return;

        const oldCompleted = requirement.completed;
        const oldText = requirement.text;
        const oldOrder = requirement.order;

        // Apply updates
        Object.assign(requirement, updates);
        task.updatedAt = new Date();

        // Add history events for different types of changes
        if ('completed' in updates && oldCompleted !== updates.completed) {
          const isCompleting = updates.completed;
          const eventType = isCompleting
            ? TaskHistoryEventType.RequirementCompleted
            : TaskHistoryEventType.RequirementUncompleted;
          const actionText = isCompleting
            ? 'completed'
            : 'marked as incomplete';

          addHistoryEventToTask(
            task,
            eventType,
            `Requirement ${actionText}: "${truncateText(requirement.text, 40)}"`,
            getCurrentAuthor(get),
            {
              requirementId,
              requirementText: requirement.text,
              oldCompleted,
              newCompleted: updates.completed,
              batchId,
            }
          );
        }

        if ('text' in updates && oldText !== updates.text) {
          addHistoryEventToTask(
            task,
            TaskHistoryEventType.RequirementUpdated,
            'Updated requirement text',
            getCurrentAuthor(get),
            {
              requirementId,
              oldText,
              newText: updates.text,
              batchId,
            }
          );
        }

        if ('order' in updates && oldOrder !== updates.order) {
          addHistoryEventToTask(
            task,
            TaskHistoryEventType.RequirementUpdated,
            'Reordered requirement',
            getCurrentAuthor(get),
            {
              requirementId,
              requirementText: truncateText(requirement.text, 40),
              oldOrder,
              newOrder: updates.order,
              batchId,
            }
          );
        }

        // Auto-update task status based on requirements and related tasks completion
        if (canAutoCompleteTask(task)) {
          const oldStatus = task.status;
          task.status = TaskStatus.Completed;

          addHistoryEventToTask(
            task,
            TaskHistoryEventType.StatusChanged,
            'Task automatically completed (all requirements and related tasks completed)',
            getCurrentAuthor(get),
            {
              oldValue: oldStatus,
              newValue: TaskStatus.Completed,
              fieldName: 'status',
              reason: 'auto_completion',
              batchId,
            }
          );
        }

        // Sync counts back to list item
        syncSelectedTaskToListItem(state);
      });
    },

    deleteRequirement: (
      taskId: string,
      requirementId: string,
      batchId?: string
    ) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const requirement = task.requirements.find(
          (req) => req.id === requirementId
        );
        if (!requirement) return;

        task.requirements = task.requirements.filter(
          (req) => req.id !== requirementId
        );
        task.updatedAt = new Date();

        // Add requirement deleted history event
        addHistoryEventToTask(
          task,
          TaskHistoryEventType.RequirementDeleted,
          `Deleted requirement: "${truncateText(requirement.text, 40)}"`,
          getCurrentAuthor(get),
          {
            requirementId,
            requirementText: requirement.text,
            requirementCompleted: requirement.completed,
            batchId,
          }
        );

        // Auto-update task status based on requirements completion
        const newStatus = determineTaskStatusFromRequirements(task);
        if (newStatus !== task.status) {
          const oldStatus = task.status;
          task.status = newStatus;

          addHistoryEventToTask(
            task,
            TaskHistoryEventType.StatusChanged,
            `Status automatically changed from ${oldStatus} to ${newStatus} due to requirement deletion`,
            getCurrentAuthor(get),
            {
              oldValue: oldStatus,
              newValue: newStatus,
              fieldName: 'status',
              reason: 'requirement_deleted',
              batchId,
            }
          );
        }

        // Sync counts back to list item
        syncSelectedTaskToListItem(state);
      });
    },

    // ===== PHASE 7c: ATTACHMENTS & COMMENTS =====

    addAttachment: (
      taskId: string,
      attachment: Omit<Attachment, 'id' | 'uploadedAt'>,
      batchId?: string
    ) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const newAttachment: Attachment = {
          ...attachment,
          id: generateId(),
          uploadedAt: new Date(),
        };

        task.attachments.push(newAttachment);
        task.updatedAt = new Date();

        // Add attachment added history event
        addHistoryEventToTask(
          task,
          TaskHistoryEventType.AttachmentAdded,
          `Added attachment: ${truncateText(newAttachment.name, 30)}`,
          getCurrentAuthor(get),
          {
            attachmentId: newAttachment.id,
            attachmentName: newAttachment.name,
            attachmentType: newAttachment.type,
            attachmentSize: newAttachment.size,
            batchId,
          }
        );

        // Sync counts back to list item
        syncSelectedTaskToListItem(state);
      });
    },

    deleteAttachment: (
      taskId: string,
      attachmentId: string,
      batchId?: string
    ) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const attachment = task.attachments.find(
          (att) => att.id === attachmentId
        );
        if (!attachment) return;

        task.attachments = task.attachments.filter(
          (att) => att.id !== attachmentId
        );
        task.updatedAt = new Date();

        // Add attachment deleted history event
        addHistoryEventToTask(
          task,
          TaskHistoryEventType.AttachmentDeleted,
          `Deleted attachment: ${truncateText(attachment.name, 30)}`,
          getCurrentAuthor(get),
          {
            attachmentId,
            attachmentName: attachment.name,
            attachmentType: attachment.type,
            attachmentSize: attachment.size,
            batchId,
          }
        );

        // Sync counts back to list item
        syncSelectedTaskToListItem(state);
      });
    },

    addComment: (
      taskId: string,
      comment: Omit<TaskComment, 'id' | 'createdAt'>
    ) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const newComment: TaskComment = {
          ...comment,
          id: generateId(),
          createdAt: new Date(),
        };

        task.comments.push(newComment);
        task.updatedAt = new Date();

        // Add comment added history event
        addHistoryEventToTask(
          task,
          TaskHistoryEventType.CommentAdded,
          'Added comment:',
          getCurrentAuthor(get),
          {
            commentId: newComment.id,
            commentPreview: truncateText(newComment.message, 50),
            commentAuthor: newComment.author,
          }
        );

        // Sync counts back to list item
        syncSelectedTaskToListItem(state);
      });
    },

    receiveComment: (taskId: string, comment: TaskComment) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        // Avoid duplicates (e.g. own comment already added optimistically)
        const exists = task.comments.some(
          (existing) => existing.id === comment.id
        );
        if (exists) return;

        task.comments.push(comment);
        task.updatedAt = new Date();

        // Sync counts back to list item
        syncSelectedTaskToListItem(state);
      });
    },

    updateComment: (
      taskId: string,
      commentId: string,
      updates: Partial<TaskComment>
    ) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const comment = task.comments.find(
          (existing) => existing.id === commentId
        );
        if (!comment) return;

        const oldMessage = comment.message;

        // Apply updates
        Object.assign(comment, updates);
        comment.updatedAt = new Date();
        comment.isEdited = true;
        task.updatedAt = new Date();

        // Add comment updated history event if message changed
        if ('message' in updates && oldMessage !== updates.message) {
          addHistoryEventToTask(
            task,
            TaskHistoryEventType.CommentUpdated,
            'Updated comment',
            getCurrentAuthor(get),
            {
              commentId,
              oldMessage,
              newMessage: updates.message,
              commentPreview: truncateText(updates.message || '', 50),
            }
          );
        }

        // Add comment deleted history event if soft-deleted
        if (
          'isDeleted' in updates &&
          updates.isDeleted === true &&
          !comment.isDeleted
        ) {
          addHistoryEventToTask(
            task,
            TaskHistoryEventType.CommentDeleted,
            'Deleted comment',
            getCurrentAuthor(get),
            {
              commentId,
              commentPreview: truncateText(oldMessage, 50),
              commentAuthor: comment.author,
            }
          );
        }
      });
    },

    deleteComment: (taskId: string, commentId: string) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const comment = task.comments.find(
          (existing) => existing.id === commentId
        );
        if (!comment) return;

        task.comments = task.comments.filter(
          (existing) => existing.id !== commentId
        );
        task.updatedAt = new Date();

        // Add comment deleted history event
        addHistoryEventToTask(
          task,
          TaskHistoryEventType.CommentDeleted,
          'Deleted comment',
          getCurrentAuthor(get),
          {
            commentId,
            commentPreview: truncateText(comment.message, 50),
            commentAuthor: comment.author,
          }
        );

        // Sync counts back to list item
        syncSelectedTaskToListItem(state);
      });
    },

    // ===== PHASE 7d: HISTORY & SELECTORS =====

    addHistoryEvent: (
      taskId: string,
      event: Omit<TaskHistoryEvent, 'id' | 'createdAt'>
    ) => {
      set((state) => {
        if (state.tasks.selectedTask?.id !== taskId) return;
        const task = state.tasks.selectedTask;

        const newEvent: TaskHistoryEvent = {
          ...event,
          id: generateId(),
          createdAt: new Date(),
        };

        task.history.push(newEvent);
        task.updatedAt = new Date();
      });
    },

    // Selectors
    getTaskById: (id: string) => {
      return get().tasks.items.find((task) => task.id === id);
    },

    getTasksByStatus: (status: TaskStatus) => {
      return get().tasks.items.filter((task) => task.status === status);
    },

    getCompletionPercentage: (taskId: string) => {
      // Try selectedTask first for accurate data
      const selectedTask = get().tasks.selectedTask;
      if (selectedTask?.id === taskId) {
        if (selectedTask.requirements.length === 0) return 0;
        const completed = selectedTask.requirements.filter(
          (req) => req.completed
        ).length;
        return Math.round((completed / selectedTask.requirements.length) * 100);
      }

      // Fallback to list item counts
      const listItem = get().tasks.items.find((task) => task.id === taskId);
      if (!listItem || listItem.requirementCount === 0) return 0;
      return Math.round(
        (listItem.completedRequirementCount / listItem.requirementCount) * 100
      );
    },

    getUniqueLocations: () => {
      return extractUniqueSortedStrings(
        get().tasks.items,
        (task) => task.location
      );
    },

    // ===== API ACTIONS =====

    fetchTasks: async () => {
      // Build server-side filter params from taskFilters slice
      const storeState = get();
      const filterParams = buildTaskFiltersParams(storeState.taskFilters);
      filterParams.page = 1;

      set((state) => {
        state.tasks.isLoading = true;
        state.tasks.error = null;
        state.tasks.items = [];
        state.tasks.currentPage = 0;
        state.tasks.hasNextPage = false;
        state.tasks.totalCount = 0;
        state.tasks._lastFetchFilters = filterParams;
      });

      try {
        const response = await tasksService.getAll(filterParams);

        set((state) => {
          state.tasks.items = response.data.items.map(
            mapTaskListItemFromResponse
          );

          state.tasks.currentPage = response.data.page;
          state.tasks.totalCount = response.data.totalCount;
          state.tasks.hasNextPage =
            response.data.page < response.data.totalPages;
          state.tasks.isLoading = false;
          state.tasks.hasLoaded = true;
          state.tasks.lastSyncAt = new Date();
        });
      } catch (error) {
        set((state) => {
          state.tasks.isLoading = false;
          state.tasks.hasLoaded = true;
          state.tasks.error = error as ApiError;
        });
      }
    },

    loadMoreTasks: async () => {
      const storeState = get();
      if (!storeState.tasks.hasNextPage || storeState.tasks.isLoading) return;

      set((state) => {
        state.tasks.isLoading = true;
      });

      const nextPage = storeState.tasks.currentPage + 1;

      try {
        const response = await tasksService.getAll({
          ...storeState.tasks._lastFetchFilters,
          page: nextPage,
        });

        set((state) => {
          const newItems = response.data.items.map(mapTaskListItemFromResponse);

          state.tasks.items.push(...newItems);
          state.tasks.currentPage = response.data.page;
          state.tasks.totalCount = response.data.totalCount;
          state.tasks.hasNextPage =
            response.data.page < response.data.totalPages;
          state.tasks.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.tasks.isLoading = false;
          state.tasks.error = error as ApiError;
        });
      }
    },

    fetchTasksForMap: async () => {
      try {
        const response = await tasksService.getForMap({
          swLat: -90,
          swLng: -180,
          neLat: 90,
          neLng: 180,
        });
        set((state) => {
          state.tasks.mapTasks = response.data.map(mapTaskForMap);
        });
      } catch (error) {
        // Geometry fetch failure is non-critical - tasks still show as markers via anchorPoint
        console.error('[fetchTasksForMap] Failed:', error);
      }
    },

    setMapTasks: (tasks: Task[]) => {
      set((state) => {
        state.tasks.mapTasks = tasks;
      });
    },

    fetchTaskById: async (id: string) => {
      set((state) => {
        state.tasks.isSyncing = true;
      });

      try {
        let apiTask;
        let historyEvents;

        if (isGuid(id)) {
          const [response, historyResponse] = await Promise.all([
            tasksService.getById(id),
            historyService.getForTask(id),
          ]);
          apiTask = response.data;
          historyEvents = historyResponse.data.events;
        } else {
          const response = await tasksService.getByShortId(id);
          apiTask = response.data;
          // History endpoint requires GUID — fetch after resolving shortId
          const historyResponse = await historyService.getForTask(apiTask.id);
          historyEvents = historyResponse.data.events;
        }
        set((state) => {
          const mappedTask: Task = {
            id: apiTask.id,
            shortId: apiTask.shortId,
            title: apiTask.title,
            description: apiTask.description,
            status: apiTask.status as TaskStatus,
            priority: apiTask.priority as Priority,
            assignedToId: apiTask.assignedToId,
            assignedToName: apiTask.assignedToName,
            assignedToAvatarUrl: apiTask.assignedToAvatarUrl ?? null,
            assignedToIsFormerMember: apiTask.assignedToIsFormerMember,
            startDate: apiTask.startDate ? new Date(apiTask.startDate) : null,
            dueDate: apiTask.dueDate ? new Date(apiTask.dueDate) : null,
            completedAt: apiTask.completedAt
              ? new Date(apiTask.completedAt)
              : null,
            location: apiTask.location ?? '',
            anchorPoint: {
              lat: apiTask.anchorPoint.lat,
              lng: apiTask.anchorPoint.lng,
            },
            geometries: apiTask.geometries.map(mapGeometryFromApi),
            requirements: apiTask.requirements.map((req) => ({
              id: req.id,
              text: req.text,
              completed: req.isCompleted,
              order: req.order,
              createdAt: new Date(req.createdAt),
            })),
            attachments: apiTask.attachments.map((att) => ({
              id: att.id,
              name: att.fileName,
              type: att.contentType,
              size: att.fileSize,
              data: '', // Storage key reference - actual data fetched separately
              uploadedAt: new Date(att.uploadedAt),
            })),
            comments: apiTask.comments.map(mapCommentFromApi),
            history: historyEvents.map((event) => ({
              id: event.id,
              taskId: event.taskId,
              eventType: event.eventType as TaskHistoryEventType,
              description: event.description,
              author: event.authorName,
              authorId: event.authorId,
              authorAvatarUrl: event.authorAvatarUrl ?? null,
              createdAt: new Date(event.createdAt),
              metadata:
                (event.metadata as TaskHistoryEvent['metadata']) ?? undefined,
            })),
            relatedTasks: (apiTask.relatedTasks ?? []).map(
              mapRelatedTaskSummaryFromResponse
            ),
            requireRelatedTasksCompletion:
              apiTask.requireRelatedTasksCompletion,
            createdAt: new Date(apiTask.createdAt),
            updatedAt: apiTask.updatedAt ? new Date(apiTask.updatedAt) : null,
          };

          // Store as selectedTask
          state.tasks.selectedTask = mappedTask;

          if (
            state.tasks.selectedId &&
            state.tasks.selectedId !== mappedTask.id
          ) {
            state.tasks.selectedId = mappedTask.id;
          }

          if (
            state.tasks.formEditingId &&
            state.tasks.formEditingId !== mappedTask.id &&
            state.tasks.formEditingId === mappedTask.shortId
          ) {
            state.tasks.formEditingId = mappedTask.id;
          }

          // Also update matching list item with fresh lightweight fields
          const listItem = state.tasks.items.find(
            (item) => item.id === mappedTask.id
          );
          if (listItem) {
            listItem.title = mappedTask.title;
            listItem.status = mappedTask.status;
            listItem.priority = mappedTask.priority;
            listItem.assignedToId = mappedTask.assignedToId;
            listItem.assignedToName = mappedTask.assignedToName;
            listItem.assignedToIsFormerMember =
              mappedTask.assignedToIsFormerMember;
            listItem.startDate = mappedTask.startDate;
            listItem.dueDate = mappedTask.dueDate;
            listItem.completedAt = mappedTask.completedAt;
            listItem.location = mappedTask.location;
            listItem.anchorPoint = mappedTask.anchorPoint;
            listItem.requirementCount = mappedTask.requirements.length;
            listItem.completedRequirementCount = mappedTask.requirements.filter(
              (req) => req.completed
            ).length;
            listItem.attachmentCount = mappedTask.attachments.length;
            listItem.commentCount = mappedTask.comments.length;
            listItem.updatedAt = mappedTask.updatedAt;
          }

          state.tasks.isSyncing = false;
        });
      } catch (error) {
        set((state) => {
          state.tasks.isSyncing = false;
          state.tasks.error = error as ApiError;
        });
      }
    },

    createTaskApi: async (
      taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
    ) => {
      set((state) => {
        state.tasks.isSyncing = true;
        state.tasks.error = null;
      });

      try {
        const response = await tasksService.create({
          title: taskData.title,
          description: taskData.description,
          status: taskData.status,
          priority: taskData.priority,
          assignedToId: taskData.assignedToId,
          startDate: taskData.startDate?.toISOString() ?? null,
          dueDate: taskData.dueDate?.toISOString() ?? null,
          location: taskData.location,
          anchorPoint: {
            lat: taskData.anchorPoint.lat,
            lng: taskData.anchorPoint.lng,
          },
          requireRelatedTasksCompletion: taskData.requireRelatedTasksCompletion,
          relatedTaskIds: taskData.relatedTasks.map(
            (relatedTask) => relatedTask.id
          ),
          geometries: taskData.geometries?.map((geo) => ({
            type: geo.type,
            name: geo.name,
            description: geo.description ?? null,
            calculatedCenter: {
              lat: geo.calculatedCenter.lat,
              lng: geo.calculatedCenter.lng,
            },
            geoData: {
              markerShape: geo.markerShape,
              markerColor: geo.markerColor,
              markerIcon: geo.markerIcon,
              coordinates: geo.markerShape
                ? {
                    lat: geo.calculatedCenter.lat,
                    lng: geo.calculatedCenter.lng,
                  }
                : undefined,
              linePoints: geo.linePoints?.map((point) => ({
                lat: point.lat,
                lng: point.lng,
              })),
              lineType: geo.lineType,
              lineStyle: geo.lineStyle,
              lineWidth: geo.lineWidth,
              lineColor: geo.lineColor,
              parentIndices: geo.parentIndices,
              shapeType: geo.shapeType,
              shapePoints: geo.shapePoints?.map((point) => ({
                lat: point.lat,
                lng: point.lng,
              })),
              shapeFill: geo.shapeFill,
              shapeFillColor: geo.shapeFillColor,
              shapeBorderStyle: geo.shapeBorderStyle,
              shapeBorderWidth: geo.shapeBorderWidth,
              shapeBorderColor: geo.shapeBorderColor,
              centerMarker: geo.centerMarker
                ? {
                    visible: geo.centerMarker.visible,
                    shape: geo.centerMarker.shape,
                    color: geo.centerMarker.color,
                    icon: geo.centerMarker.icon,
                  }
                : undefined,
            },
          })),
          requirements: taskData.requirements?.map((req) => ({
            text: req.text,
            isCompleted: req.completed,
            order: req.order,
          })),
        });

        // Add the created task as a lightweight list item
        const apiTask = response.data;
        set((state) => {
          state.tasks.items.push(mapTaskResponseToListItem(apiTask));
          state.tasks.totalCount += 1;

          // Sync new task to map markers (drives symbol layer)
          state.tasks.mapTasks.push(mapTaskForMap(apiTask));

          state.tasks.isSyncing = false;
        });

        return apiTask.id;
      } catch (error) {
        set((state) => {
          state.tasks.isSyncing = false;
          state.tasks.error = error as ApiError;
        });
        return null;
      }
    },

    updateTaskApi: async (id: string, updates: Partial<Task>) => {
      // Optimistic update - apply changes immediately
      get().tasks.updateTask(id, updates);

      set((state) => {
        state.tasks.isSyncing = true;
      });

      try {
        const response = await tasksService.update(id, {
          title: updates.title,
          description: updates.description,
          status: updates.status,
          priority: updates.priority,
          assignedToId: updates.assignedToId,
          startDate: updates.startDate?.toISOString() ?? null,
          dueDate: updates.dueDate?.toISOString() ?? null,
          location: updates.location ?? null,
          anchorPoint: updates.anchorPoint
            ? { lat: updates.anchorPoint.lat, lng: updates.anchorPoint.lng }
            : undefined,
          requireRelatedTasksCompletion: updates.requireRelatedTasksCompletion,
          relatedTaskIds: updates.relatedTasks?.map(
            (relatedTask) => relatedTask.id
          ),
        });
        const updatedTask = response.data.task;

        set((state) => {
          state.tasks.isSyncing = false;

          // Patch server-resolved fields on selectedTask
          if (state.tasks.selectedTask?.id === id) {
            state.tasks.selectedTask.assignedToName =
              updatedTask.assignedToName;
            state.tasks.selectedTask.assignedToIsFormerMember =
              updatedTask.assignedToIsFormerMember;
          }

          // Sync updated fields to map markers (drives symbol layer)
          const mapTask = state.tasks.mapTasks.find((t) => t.id === id);
          if (mapTask) {
            if (updates.title !== undefined) mapTask.title = updates.title;
            if (updates.status !== undefined) mapTask.status = updates.status;
            if (updates.priority !== undefined)
              mapTask.priority = updates.priority;
            if (updates.anchorPoint !== undefined) {
              mapTask.anchorPoint = {
                lat: updates.anchorPoint.lat,
                lng: updates.anchorPoint.lng,
              };
            }
            if (updates.geometries !== undefined) {
              mapTask.geometries = updates.geometries;
            }
            mapTask.updatedAt = new Date();
          }
        });

        return true;
      } catch (error) {
        // Rollback would require storing previous state - for now just set error
        set((state) => {
          state.tasks.isSyncing = false;
          state.tasks.error = error as ApiError;
        });
        return false;
      }
    },

    deleteTaskApi: async (id: string) => {
      // Optimistic delete
      get().tasks.deleteTask(id);

      set((state) => {
        state.tasks.isSyncing = true;
      });

      try {
        await tasksService.delete(id);

        set((state) => {
          state.tasks.isSyncing = false;
        });

        return true;
      } catch (error) {
        // Rollback would require restoring task - for now just set error
        set((state) => {
          state.tasks.isSyncing = false;
          state.tasks.error = error as ApiError;
        });
        return false;
      }
    },

    // ===== KANBAN PER-STATUS PAGINATION =====

    fetchKanbanData: async () => {
      const storeState = get();
      const filterParams = buildTaskFiltersParams(storeState.taskFilters);

      const statuses = [
        TaskStatus.Pending,
        TaskStatus.InProgress,
        TaskStatus.Delayed,
        TaskStatus.Completed,
        TaskStatus.Archived,
      ];

      set((state) => {
        for (const status of statuses) {
          state.tasks.kanban.columns[status] = {
            items: [],
            currentPage: 0,
            hasNextPage: false,
            totalCount: 0,
            isLoading: true,
          };
        }
      });

      const results = await Promise.allSettled(
        statuses.map((status) =>
          tasksService.getAll({
            ...filterParams,
            statuses: status,
            page: 1,
            pageSize: KANBAN_PAGE_SIZE,
          })
        )
      );

      set((state) => {
        statuses.forEach((status, index) => {
          const result = results[index];

          if (result && result.status === 'fulfilled') {
            const response = result.value;
            state.tasks.kanban.columns[status] = {
              items: response.data.items.map(mapTaskListItemFromResponse),
              currentPage: response.data.page,
              hasNextPage: response.data.page < response.data.totalPages,
              totalCount: response.data.totalCount,
              isLoading: false,
            };
          } else {
            const column = state.tasks.kanban.columns[status];
            if (column) {
              column.isLoading = false;
            }
          }
        });
      });
    },

    loadMoreKanbanColumn: async (status: TaskStatus) => {
      const storeState = get();
      const column = storeState.tasks.kanban.columns[status];

      if (!column || !column.hasNextPage || column.isLoading) return;

      const expectedPage = column.currentPage;

      set((state) => {
        const targetColumn = state.tasks.kanban.columns[status];
        if (targetColumn) {
          targetColumn.isLoading = true;
        }
      });

      const filterParams = buildTaskFiltersParams(storeState.taskFilters);
      const nextPage = expectedPage + 1;

      try {
        const response = await tasksService.getAll({
          ...filterParams,
          statuses: status,
          page: nextPage,
          pageSize: KANBAN_PAGE_SIZE,
        });

        // Discard stale response if column was reset by fetchKanbanData during await
        const currentColumn = get().tasks.kanban.columns[status];
        if (!currentColumn || currentColumn.currentPage !== expectedPage)
          return;

        set((state) => {
          const targetColumn = state.tasks.kanban.columns[status];
          if (targetColumn) {
            const newItems = response.data.items.map(
              mapTaskListItemFromResponse
            );
            targetColumn.items.push(...newItems);
            targetColumn.currentPage = response.data.page;
            targetColumn.hasNextPage =
              response.data.page < response.data.totalPages;
            targetColumn.totalCount = response.data.totalCount;
            targetColumn.isLoading = false;
          }
        });
      } catch (error) {
        set((state) => {
          const targetColumn = state.tasks.kanban.columns[status];
          if (targetColumn) {
            targetColumn.isLoading = false;
          }
          state.tasks.error = error as ApiError;
        });
      }
    },

    // API state helpers
    setLoading: (loading: boolean) => {
      set((state) => {
        state.tasks.isLoading = loading;
      });
    },

    setError: (error: ApiError | null) => {
      set((state) => {
        state.tasks.error = error;
      });
    },

    clearError: () => {
      set((state) => {
        state.tasks.error = null;
      });
    },
  },
});
